/**
 * Minimal anchored popover for the viewer toolbar.
 *
 * The shared primitives deliberately do not ship one, and the toolbar needs
 * three small transient panels (window/level, histogram, overlay settings).
 * Kept local rather than added to `primitives.tsx`, which W0 owns.
 */

import * as React from "react";
import { cn } from "@/lib/utils";

export function Popover({
  open,
  onClose,
  align = "left",
  className,
  children,
}: {
  open: boolean;
  onClose: () => void;
  align?: "left" | "right";
  className?: string;
  children: React.ReactNode;
}) {
  const ref = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      const node = ref.current;
      // The anchor button lives outside this node, so ignore clicks that land
      // on it — otherwise the toggle closes and reopens in the same gesture.
      if (node && !node.contains(event.target as Node) && !node.parentElement?.contains(event.target as Node)) {
        onClose();
      }
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div
      ref={ref}
      role="dialog"
      className={cn(
        "absolute bottom-full z-30 mb-1 rounded border border-ads-border-strong bg-ads-surface p-3 shadow-lg",
        align === "right" ? "right-0" : "left-0",
        className,
      )}
    >
      {children}
    </div>
  );
}
